import { ConfigProvider } from 'shared/config/ConfigContext';
import flightData from 'shared/seo/data/flight.json';
import { LinkTree } from 'shared/seo/LinkTree';
import { PopularCities } from 'shared/seo/PopularCities';
import { PopularCountries } from 'shared/seo/PopularCountries';
import { DestinationPage } from '@/components/DestinationPage';
import { countryLevel1Factory, flightSearchFactory } from '@/components/factory';
import { feConfig } from '@/config';
import { flightCountries, flightMapping, getCountriesForDestination } from '@/data';
import { verticals } from '@/data/verticals';
import type { CountryPageProps, SeoPageContext } from '@/interfaces/pages';

export async function getStaticPaths() {
    const destinations = flightCountries.map((place) => ({
        params: { countryLevel1: place.countryPath },
    }));

    return {
        paths: destinations,
        fallback: false,
    };
}

export async function getStaticProps(context: SeoPageContext) {
    const destination = flightCountries.find((place) => place.countryPath === context.params.countryLevel1.toLowerCase());

    const popularCities = (flightMapping[destination.id] ?? []).slice(0, 11);
    const popularCountries = getCountriesForDestination(destination, flightCountries);

    return {
        props: {
            destination,
            popularCities,
            popularCountries,
            config: feConfig,
        },
    };
}

const CountryLevel1 = ({ destination, popularCities, popularCountries, config }: CountryPageProps) => {
    const factory = countryLevel1Factory(destination, flightSearchFactory());

    return (
        <ConfigProvider config={config}>
            <DestinationPage factory={factory} vertical={verticals.flight} data={flightData}>
                <PopularCities
                    destination={destination}
                    cities={popularCities}
                    cdnUrl={flightData.cdnPath}
                    verticalUrl={flightData.basePath}
                    lang={flightData.lang}
                />
                <PopularCountries
                    destination={destination}
                    countries={popularCountries}
                    cdnUrl={flightData.cdnPath}
                    verticalUrl={flightData.basePath}
                    lang={flightData.lang}
                />
                <LinkTree
                    heading={flightData.lang.linkTree.heading}
                    description={flightData.lang.linkTree.description}
                    data={flightData.linkTree}
                    url={flightData.basePath}
                />
            </DestinationPage>
        </ConfigProvider>
    );
};

export default CountryLevel1;
